<template>
  <DsPageContent>
    <DsHeroWithMedia
      v-if="content.hero"
      :screen="screen"
      :title="content.hero.title"
      :image="content.hero.image"
      :links="content.hero.links"
      :eyebrow="content.hero.eyebrow"
      :icon="content.hero.icon"
      class="ds-margin-bottom-48 ds-md-margin-bottom-96"
    >
      <template
        v-if="content.hero.description"
        #description
      >
        <RichText
          :data="content.hero.description"
          class="ds-text-medium"
        />
      </template>
    </DsHeroWithMedia>
    <DsSection
      v-if="content.benefitsCollection"
      :with-space-top="false"
    >
      <DsGrid
        :column-width="{ small: 12, medium: 4, large: 3 }"
        :items="content.benefitsCollection"
      >
        <template #default="{ item }">
          <DsCardTextIcon
            v-if="item"
            :icon="item.icon"
            :title="item.title"
          >
            <RichText
              v-if="item.description"
              :data="item.description"
            />
          </DsCardTextIcon>
        </template>
      </DsGrid>
    </DsSection>
    <DsSectionImage
      v-if="content.introVideo"
      theme-variant="light-grey"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsVideoPlayer
            :aria-label-play="labels.play"
            :aria-label-close="labels.close"
            v-bind="content.introVideo"
            :screen="screen"
            class="ds-width-full"
          />
        </div>
      </div>
    </DsSectionImage>
    <DsSection
      v-if="content.logoBar1"
      theme="grey"
    >
      <DsLogoBar
        :items="content.logoBar1.items"
        align-center
      />
    </DsSection>
    <DsSection
      :with-space="false"
      theme="grey"
      aria-hidden="true"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsDivider theme="light" />
        </div>
      </div>
    </DsSection>
    <DsQuote
      v-if="content.quote"
      :contained="false"
      theme="grey"
      :logo="content.quote.logo"
      :text="content.quote.quote"
      :author="content.quote.author"
      :extra-info="content.quote.role"
    />
    <DsSection
      :with-space="false"
      theme="grey"
      aria-hidden="true"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsDivider theme="light" />
        </div>
      </div>
    </DsSection>
    <DsSectionGroup
      v-if="content.contentBlock1"
      :title="content.contentBlock1.title"
      :subtitle="content.contentBlock1.description"
      :link="content.contentBlock1.link"
      theme="grey"
    >
      <DsGrid
        :column-width="{ small: 12, medium: 4, large: 4 }"
        :items="content.contentBlock1.items"
      >
        <template #default="{ item }">
          <div>
            <h4
              class="ds-heading-xsmall ds-margin-bottom-12"
              v-text="item.title"
            />
            <RichText :data="item.description" />
          </div>
        </template>
      </DsGrid>
    </DsSectionGroup>
    <DsSectionGroup
      v-if="content.contentBlock2"
      :title="content.contentBlock2.title"
      :subtitle="content.contentBlock2.description"
      with-title-size-large
      centered
    >
      <DsMediaTextBlock
        v-for="(item, index) in content.contentBlock2.items"
        :key="item.title"
        :orientation="index % 2 === 1 ? 'left' : 'right'"
        :title="item.title"
        :image="item.image"
        :screen="screen"
        :link="item.link"
        :class="index !== 0 && 'ds-padding-top-48 ds-md-padding-top-96'"
      >
        <RichText
          :data="item.description"
          class="ds-list-style-checked ds-list-style-checked--green ds-list-font-default"
        />
      </DsMediaTextBlock>
    </DsSectionGroup>

    <DsSection
      v-if="content.contentBlock3"
      theme="grey"
    >
      <DsMediaTextBlock
        :title="content.contentBlock3.title"
        :image="content.contentBlock3.image"
        :link="content.contentBlock3.link"
        :screen="screen"
        center-aligned
        orientation="bottom"
      >
        <RichText :data="content.contentBlock3.description" />
      </DsMediaTextBlock>
    </DsSection>
    <DsSection v-if="content.contentBlock4">
      <DsMediaTextBlock
        :title="content.contentBlock4.title"
        :image="content.contentBlock4.image"
        :link="content.contentBlock4.link"
        :screen="screen"
      >
        <RichText :data="content.contentBlock4.description" />
      </DsMediaTextBlock>
    </DsSection>

    <template v-if="content.contentBlock4 && content.uspsCollection">
      <DsSection :with-space="false">
        <div class="ds-grid">
          <div class="ds-grid__col">
            <DsDivider />
          </div>
        </div>
      </DsSection>

      <DsSection v-if="content.uspsCollection">
        <DsGrid
          :column-width="{ small: 12, medium: 4, large: 3 }"
          :items="content.uspsCollection"
        >
          <template #default="{ item }">
            <DsCardTextIcon
              v-if="item"
              :icon="item.icon"
              :title="item.title"
            >
              <RichText
                v-if="item.description"
                :data="item.description"
              />
            </DsCardTextIcon>
          </template>
        </DsGrid>
      </DsSection>
    </template>
    <DsSection
      v-if="content.image"
      :with-space-top="false"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsImageElement
            :screen="screen"
            :image="content.image"
          />
        </div>
      </div>
    </DsSection>

    <DsSectionGroup
      v-if="content.contentBlock5"
      :title="content.contentBlock5.title"
      :subtitle="content.contentBlock5.description"
      :link="content.contentBlock5.link"
      theme="grey"
    >
      <DsGrid
        :column-width="{ small: 12, medium: 4, large: 4 }"
        :items="content.contentBlock5.items"
      >
        <template #default="{ item }">
          <div v-if="item">
            <h4
              class="ds-heading-xsmall ds-margin-bottom-12"
              v-text="item.title"
            />
            <RichText :data="item.description" />
          </div>
        </template>
      </DsGrid>
    </DsSectionGroup>

    <DsSection v-if="content.logoBar2">
      <DsLogoBar
        :items="content.logoBar2.items"
        :link="content.logoBar2.link"
        :title="content.logoBar2.title"
        orientation="horizontal"
      >
        <template #description>
          <RichText :data="content.logoBar2.description" />
        </template>
      </DsLogoBar>
    </DsSection>
    <DsSection :with-space="false">
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsDivider />
        </div>
      </div>
    </DsSection>
    <KnowledgeHubRelatedContent
      :related-content="content.relatedContent"
      :see-all-label="labels.seeAllKnowledgeHubArticles"
    />
    <DsSection v-if="content.contentBlock6">
      <DsMediaTextBlock
        :title="content.contentBlock6.title"
        :image="content.contentBlock6.image"
        :link="content.contentBlock6.link"
        :screen="screen"
        orientation="left"
      >
        <RichText :data="content.contentBlock6.description" />
      </DsMediaTextBlock>
    </DsSection>
    <!-- Call to Action -->
    <DsCallToAction
      v-if="content.callToAction"
      :title="content.callToAction.title"
      :text="content.callToAction.text"
      :primary-link="content.callToAction.primaryLink"
      :secondary-link="content.callToAction.secondaryLink"
    />
  </DsPageContent>
</template>
<script setup>
import DefinitionsIndustry from '../../../../core/data-layer/definitions/industry'
import {
  DsCallToAction,
  DsCardTextIcon,
  DsDivider,
  DsGrid,
  DsHeroWithMedia,
  DsImageElement,
  DsLogoBar,
  DsMediaTextBlock,
  DsPageContent,
  DsQuote,
  DsSection,
  DsSectionGroup,
  DsSectionImage,
  DsVideoPlayer
} from 'awds'
const { locale } = useI18n()
const { useStandardAsyncData, onBeforeAdyenData, onAfterAdyenData, labels } = useMainPageData()
const { screen } = useScreen()

await onBeforeAdyenData({ globalDataContentType: DefinitionsIndustry.PageIndustryRetailPayments.contentType })
const { data } = await useAsyncData('industries-retail' + locale.value, async () => {
  const data = await useStandardAsyncData(DefinitionsIndustry.PageIndustryRetailPayments)
  const pageData = data?.pageData
  return {
    pageData
  }
})

// Computed
const content = computed(() => {
  return data?.value?.pageData?.content || {}
})

await onAfterAdyenData(data.value)
</script>
